import { defineComponent as _defineComponent } from 'vue';
import { resolveDynamicComponent as _resolveDynamicComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, mergeProps as _mergeProps, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment } from "vue";
const _hoisted_1 = { class: "grid min-h-full grid-cols-[auto_1fr] bg-white" };
const _hoisted_2 = {
    class: "px-5 pt-6 pb-4 min-w-0",
    "data-testid": "user-detail-view"
};
const _hoisted_3 = { class: "max-w-270 mx-auto" };
const _hoisted_4 = { class: "flex mb-3" };
const _hoisted_5 = { class: "flex items-start justify-between gap-3 py-1 mb-8" };
const _hoisted_6 = ["title"];
const _hoisted_7 = {
    key: 0,
    "data-testid": "user-detail-loading",
    class: "flex flex-col items-center justify-center py-24"
};
const _hoisted_8 = { class: "text-sm text-(--colors-text-body-subtle)" };
const _hoisted_9 = {
    key: 1,
    "data-testid": "user-detail-error",
    class: "flex flex-col items-center justify-center py-24"
};
const _hoisted_10 = { class: "text-sm text-(--colors-text-body) mb-4" };
const _hoisted_11 = {
    key: 2,
    class: "flex flex-col gap-5"
};
const _hoisted_12 = { class: "flex flex-col gap-2.5" };
const _hoisted_13 = { class: "text-(length:--size-m) font-(--weight-medium) leading-(--line-height-l) text-(--text-text-black-default)" };
const _hoisted_14 = { class: "grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4" };
const _hoisted_15 = {
    key: 1,
    class: "px-1 text-sm leading-5"
};
import { computed, onMounted, ref, shallowRef, unref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { RouterLink, useRoute } from "vue-router";
import { Avatar, Badge, Button, InlineLink, Dropdown, DynamicFormField, TextInput, Tooltip, formatPhoneForDisplay, } from "@eudonet/opal";
import { logger } from "@/infrastructure/services/LoggerService";
import { configService } from "@/infrastructure/services/ConfigService";
import { moduleRegistry } from "@/router/moduleRegistry";
import { useUsersAdminStore } from "../../stores/useUsersAdminStore";
import { MockBusinessRolesService } from "../../infrastructure/adapters/MockBusinessRolesService";
import { useUserActions } from "../composables/useUserActions";
import UserStateBadge from "../components/UserStateBadge.vue";
import UserActionConfirmModal from "../components/UserActionConfirmModal.vue";
import UserPropertiesModal from "../components/UserPropertiesModal.vue";
import UserPermissionsModal from "../components/UserPermissionsModal.vue";
import UserReadonlyChipsField from "../components/UserReadonlyChipsField.vue";
const EMPTY_VALUE = "–";
export default /*@__PURE__*/ _defineComponent({
    __name: 'UserDetailView',
    setup(__props) {
        const { t } = useI18n();
        const route = useRoute();
        const store = useUsersAdminStore();
        const SidebarComponent = shallowRef(moduleRegistry.getComponentForSlot("sidebar"));
        const userId = computed(() => String(route.params.userId ?? ""));
        // unref() bridges the real Pinia store (auto-unwrapped refs) and the plain-object
        // mock used in the spec (raw refs) — both resolve to the underlying value here.
        const user = computed(() => unref(store.currentUser));
        const isLoadingUser = computed(() => unref(store.isLoadingUser));
        const userError = computed(() => unref(store.userError));
        const isPropertiesOpen = ref(false);
        const isPermissionsOpen = ref(false);
        // On success, reload the user so the Status field reflects the new state. The record stays on
        // screen if that reload fails (the error state needs `!user`), so the failure is only logged and
        // the action's own success toast stays true.
        const userActions = useUserActions({
            onActionSuccess: () => store
                .loadUser(userId.value, { keepCurrent: true })
                .catch((err) => logger.error("Failed to refresh user detail after an admin action:", err)),
            onShowProperties: () => {
                isPropertiesOpen.value = true;
            },
            onEditPermissions: () => {
                isPermissionsOpen.value = true;
            },
        });
        const { canManageUsers, buildActionsFor, isConfirmOpen, confirmTitleKey, confirmBodyKey, confirmLabelKey, confirmDanger, isRunning, confirm: onConfirmAction, cancel: onCancelAction, } = userActions;
        const userActionItems = computed(() => (user.value ? buildActionsFor(user.value) : []));
        const fullName = computed(() => [user.value?.firstName, user.value?.lastName].filter(Boolean).join(" "));
        const phone = computed(() => {
            const cc = user.value?.phoneCountryCode?.trim() ?? "";
            const nn = user.value?.phoneNationalNumber?.trim() ?? "";
            if (!cc && !nn)
                return "";
            // No stored country code means the number's origin is unknown — never assume a country.
            // formatPhoneForDisplay's no-dial-code fallback is a DEFAULT_COUNTRY guess (see
            // phone-format.ts), not a guarantee, and this view has no per-tenant convention to lean on
            // instead — so render the raw digits rather than risk misformatting a foreign number.
            if (!cc)
                return nn;
            const dial = cc.startsWith("+") ? cc : `+${cc}`;
            return formatPhoneForDisplay(`${dial}${nn.replace(/\D/g, "")}`);
        });
        function readonlyField(key, type, value) {
            const config = {
                key,
                type,
                label: t(`accessManager.userDetail.fields.${key}`),
                size: "md",
                fullWidth: true,
                readonly: true,
            };
            return { config, value: value || EMPTY_VALUE };
        }
        const identityFields = computed(() => [
            readonlyField("firstName", "text", user.value?.firstName),
            readonlyField("lastName", "text", user.value?.lastName),
            readonlyField("email", "email", user.value?.email),
            readonlyField("phone", "phone", phone.value),
            readonlyField("department", "text", user.value?.department),
            readonlyField("jobTitle", "text", user.value?.jobTitle),
        ]);
        const isBusinessRolesEnabled = configService.isBusinessRolesMockEnabled;
        const businessRolesService = new MockBusinessRolesService();
        const businessRoles = ref([]);
        async function loadBusinessRoles(id) {
            try {
                const roles = await businessRolesService.listForUser(id);
                if (id === userId.value)
                    businessRoles.value = roles;
            }
            catch (err) {
                logger.error("Failed to load business roles:", err);
            }
        }
        function load() {
            // Rejection is captured in store.userError and surfaced by the error state above;
            // catch here only to avoid an unhandled promise rejection.
            store.loadUser(userId.value).catch(() => { });
            businessRoles.value = [];
            if (isBusinessRolesEnabled)
                void loadBusinessRoles(userId.value);
        }
        // A param-only route change (history back/forward between two users) reuses this view, so the
        // previous user's dialogs would otherwise reopen on the next one.
        watch(userId, () => {
            isPermissionsOpen.value = false;
            isPropertiesOpen.value = false;
            load();
        });
        onMounted(load);
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                (SidebarComponent.value)
                    ? (_openBlock(), _createBlock(_resolveDynamicComponent(SidebarComponent.value), { key: 0 }))
                    : _createCommentVNode("v-if", true),
                _createElementVNode("div", _hoisted_2, [
                    _createElementVNode("div", _hoisted_3, [
                        _createElementVNode("div", _hoisted_4, [
                            _createVNode(_unref(InlineLink), {
                                as: _unref(RouterLink),
                                "icon-left": "ri-arrow-left-line",
                                to: { name: 'AccessManagerUsers' },
                                "data-testid": "user-detail-back"
                            }, {
                                default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(_ctx.$t("accessManager.userDetail.back")), 1 /* TEXT */)
                                ]),
                                _: 1 /* STABLE */
                            }, 8 /* PROPS */, ["as"])
                        ]),
                        _createElementVNode("div", _hoisted_5, [
                            _createElementVNode("h1", {
                                class: "truncate text-(length:--size-5xl) font-bold leading-(--line-height-5xl) text-(--text-text-heading)",
                                title: user.value ? fullName.value : undefined
                            }, _toDisplayString(user.value ? fullName.value : _ctx.$t("accessManager.userDetail.title")), 9 /* TEXT, PROPS */, _hoisted_6),
                            (user.value)
                                ? (_openBlock(), _createBlock(_unref(Tooltip), {
                                    key: 0,
                                    class: "shrink-0",
                                    text: _unref(t)('common.permissions.noEditRights'),
                                    disabled: _unref(canManageUsers),
                                    tabindex: _unref(canManageUsers) ? undefined : 0
                                }, {
                                    default: _withCtx(() => [
                                        _createVNode(_unref(Dropdown), {
                                            items: userActionItems.value,
                                            "menu-align": "right",
                                            "data-testid": "user-detail-actions-dropdown"
                                        }, {
                                            trigger: _withCtx(({ triggerProps }) => [
                                                _createVNode(_unref(Button), _mergeProps(triggerProps, {
                                                    size: "lg",
                                                    variant: "secondary",
                                                    label: _ctx.$t('accessManager.userDetail.actions.label'),
                                                    "icon-left": "ri-settings-3-line",
                                                    "dropdown-icon": "",
                                                    disabled: !_unref(canManageUsers)
                                                }), null, 16 /* FULL_PROPS */, ["label", "disabled"])
                                            ]),
                                            _: 1 /* STABLE */
                                        }, 8 /* PROPS */, ["items"])
                                    ]),
                                    _: 1 /* STABLE */
                                }, 8 /* PROPS */, ["text", "disabled", "tabindex"]))
                                : _createCommentVNode("v-if", true)
                        ]),
                        (isLoadingUser.value && !user.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                                _cache[2] || (_cache[2] = _createElementVNode("i", { class: "ri-loader-4-line animate-spin text-4xl text-(--colors-text-body-subtle) mb-4" }, null, -1 /* CACHED */)),
                                _createElementVNode("p", _hoisted_8, _toDisplayString(_ctx.$t("accessManager.userDetail.loading")), 1 /* TEXT */)
                            ]))
                            : (userError.value && !user.value)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                                    _cache[3] || (_cache[3] = _createElementVNode("i", { class: "ri-error-warning-line text-4xl text-(--colors-text-error) mb-4" }, null, -1 /* CACHED */)),
                                    _createElementVNode("p", _hoisted_10, _toDisplayString(_ctx.$t("accessManager.userDetail.loadFailed")), 1 /* TEXT */),
                                    _createVNode(_unref(Button), {
                                        variant: "secondary",
                                        label: _ctx.$t('accessManager.userDetail.retry'),
                                        onClick: load
                                    }, null, 8 /* PROPS */, ["label"])
                                ]))
                                : (user.value)
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                                        _createElementVNode("div", _hoisted_12, [
                                            _createElementVNode("span", _hoisted_13, _toDisplayString(_ctx.$t("accessManager.userDetail.fields.picture")), 1 /* TEXT */),
                                            _createVNode(_unref(Avatar), {
                                                img: user.value.avatarUri,
                                                name: fullName.value,
                                                size: "2xl",
                                                "avatar-style": "square"
                                            }, null, 8 /* PROPS */, ["img", "name"])
                                        ]),
                                        _createElementVNode("div", _hoisted_14, [
                                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(identityFields.value, (field) => {
                                                return (_openBlock(), _createElementBlock(_Fragment, {
                                                    key: field.config.key
                                                }, [
                                                    (field.config.type === 'phone')
                                                        ? (_openBlock(), _createBlock(_unref(TextInput), {
                                                            key: 0,
                                                            "model-value": field.value,
                                                            label: field.config.label,
                                                            "icon-left": "ri-phone-fill",
                                                            size: "md",
                                                            readonly: "",
                                                            "full-width": ""
                                                        }, null, 8 /* PROPS */, ["model-value", "label"]))
                                                        : (_openBlock(), _createBlock(_unref(DynamicFormField), {
                                                            key: 1,
                                                            field: field.config,
                                                            "model-value": field.value
                                                        }, null, 8 /* PROPS */, ["field", "model-value"]))
                                                ], 64 /* STABLE_FRAGMENT */));
                                            }), 128 /* KEYED_FRAGMENT */)),
                                            _createVNode(UserReadonlyChipsField, {
                                                label: _ctx.$t('accessManager.userDetail.fields.roles'),
                                                "data-testid": "user-detail-roles"
                                            }, {
                                                default: _withCtx(() => [
                                                    (_unref(isBusinessRolesEnabled) && businessRoles.value.length > 0)
                                                        ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(businessRoles.value, (role) => {
                                                            return (_openBlock(), _createBlock(_unref(Badge), {
                                                                key: role,
                                                                label: role,
                                                                theme: "cyan",
                                                                size: "sm",
                                                                "show-close-icon": false,
                                                                truncate: ""
                                                            }, null, 8 /* PROPS */, ["label"]));
                                                        }), 128 /* KEYED_FRAGMENT */))
                                                        : (_openBlock(), _createElementBlock("span", _hoisted_15, _toDisplayString(EMPTY_VALUE)))
                                                ]),
                                                _: 1 /* STABLE */
                                            }, 8 /* PROPS */, ["label"]),
                                            _createVNode(UserReadonlyChipsField, {
                                                label: _ctx.$t('accessManager.userDetail.fields.status'),
                                                "data-testid": "user-detail-status"
                                            }, {
                                                default: _withCtx(() => [
                                                    _createVNode(UserStateBadge, {
                                                        enabled: user.value.enabled,
                                                        "is-invited": user.value.isInvited
                                                    }, null, 8 /* PROPS */, ["enabled", "is-invited"])
                                                ]),
                                                _: 1 /* STABLE */
                                            }, 8 /* PROPS */, ["label"])
                                        ])
                                    ]))
                                    : _createCommentVNode("v-if", true),
                        (user.value)
                            ? (_openBlock(), _createBlock(UserPermissionsModal, {
                                key: 3,
                                modelValue: isPermissionsOpen.value,
                                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((isPermissionsOpen).value = $event)),
                                "user-id": userId.value,
                                roles: user.value.roles,
                                "assigned-roles": user.value.assignedRoles
                            }, null, 8 /* PROPS */, ["modelValue", "user-id", "roles", "assigned-roles"]))
                            : _createCommentVNode("v-if", true),
                        _createVNode(UserPropertiesModal, {
                            modelValue: isPropertiesOpen.value,
                            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((isPropertiesOpen).value = $event)),
                            properties: user.value?.properties ?? null
                        }, null, 8 /* PROPS */, ["modelValue", "properties"]),
                        _createVNode(UserActionConfirmModal, {
                            "model-value": _unref(isConfirmOpen),
                            "title-key": _unref(confirmTitleKey),
                            "body-key": _unref(confirmBodyKey),
                            "confirm-key": _unref(confirmLabelKey),
                            danger: _unref(confirmDanger),
                            loading: _unref(isRunning),
                            onAccept: _unref(onConfirmAction),
                            onCancel: _unref(onCancelAction)
                        }, null, 8 /* PROPS */, ["model-value", "title-key", "body-key", "confirm-key", "danger", "loading", "onAccept", "onCancel"])
                    ])
                ])
            ]));
        };
    }
});
